// ─── AutoChinBot Telegram Setup Panel ──────────────────────────
// Lets the (non-technical) user turn the 2-way Telegram bot on/off for a
// chosen Telegram account. Internally it calls the `telegram-bot` edge
// function to register/remove the webhook and shows the live status.

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { telegramBot, type WebhookInfoResult, type WebhookHealth } from '@/lib/telegram-bot-client';
import { cn } from './types';

interface TelegramAccount {
  id: number;
  name: string;
  chat_id: string;
  is_active: boolean;
}

export default function AutoChinTelegramPanel() {
  const [accounts, setAccounts] = useState<TelegramAccount[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [info, setInfo] = useState<WebhookInfoResult | null>(null);
  const [health, setHealth] = useState<WebhookHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [healing, setHealing] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const loadAccounts = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('telegram_accounts')
        .select('id, name, chat_id, is_active')
        .order('id', { ascending: true });
      if (error) throw error;
      const list = (data || []) as TelegramAccount[];
      setAccounts(list);
      if (list.length > 0) setSelectedId((prev) => prev ?? list[0].id);
    } catch {
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshInfo = useCallback(async (id: number) => {
    // Ask the engine to health-check this webhook. The server is throttled, so a
    // frequent poll here stays cheap — and if a problem is found it is RE-REGISTERED
    // automatically right inside this call.
    const healthRes = await telegramBot.webhookHealth(id, false);
    const h = healthRes.result ?? null;
    setHealth(h);
    if (h?.healed) {
      setMsg({ type: 'ok', text: 'Webhook bị lỗi đã được tự động đăng ký lại. Bot sẵn sàng nhận lệnh.' });
    }
    setInfo(null);
    const res = await telegramBot.webhookInfo(id);
    setInfo(res);
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    if (selectedId) refreshInfo(selectedId);
  }, [selectedId, refreshInfo]);

  // Keep the status fresh while the panel is open: re-check every 60s. Any
  // broken webhook is healed automatically by that same call.
  useEffect(() => {
    if (!selectedId) return;
    const timer = setInterval(() => { void refreshInfo(selectedId); }, 60_000);
    return () => clearInterval(timer);
  }, [selectedId, refreshInfo]);

  const handleActivate = async () => {
    if (!selectedId || busy) return;
    setBusy(true);
    setMsg(null);
    const res = await telegramBot.setWebhook(selectedId);
    if (res.success) {
      setMsg({ type: 'ok', text: 'Đã bật Telegram 2 chiều. Giờ hãy chat /start với bot để kiểm tra.' });
      await refreshInfo(selectedId);
    } else {
      setMsg({ type: 'err', text: res.error || 'Không bật được webhook.' });
    }
    setBusy(false);
  };

  const handleDeactivate = async () => {
    if (!selectedId || busy) return;
    setBusy(true);
    setMsg(null);
    const res = await telegramBot.deleteWebhook(selectedId);
    if (res.success) {
      setMsg({ type: 'ok', text: 'Đã ngắt Telegram 2 chiều.' });
      await refreshInfo(selectedId);
    } else {
      setMsg({ type: 'err', text: res.error || 'Không ngắt được webhook.' });
    }
    setBusy(false);
  };

  const handleForceHeal = async () => {
    if (!selectedId || healing) return;
    setHealing(true);
    setMsg(null);
    try {
      // force=true bypasses the server-side throttle so we really query Telegram
      // right now — and re-register the webhook if anything looks broken.
      const healthRes = await telegramBot.webhookHealth(selectedId, true);
      const h = healthRes.result ?? null;
      setHealth(h);

      if (!healthRes.success) {
        setMsg({ type: 'err', text: healthRes.error || 'Không kiểm tra được webhook.' });
      } else if (!h) {
        setMsg({ type: 'err', text: 'Không lấy được trạng thái webhook.' });
      } else if (h.healed) {
        setMsg({ type: 'ok', text: 'Đã phát hiện lỗi và tự đăng ký lại webhook. Bot sẵn sàng nhận lệnh.' });
      } else if (h.healthy) {
        setMsg({ type: 'ok', text: 'Webhook đang khoẻ, không cần sửa. Hãy thử gõ /start với bot.' });
      } else if (h.error) {
        setMsg({ type: 'err', text: h.error });
      } else {
        setMsg({ type: 'err', text: h.reasons[0] ? `Webhook có vấn đề: ${h.reasons[0]}` : 'Webhook có vấn đề nhưng chưa sửa được.' });
      }

      setInfo(null);
      const res = await telegramBot.webhookInfo(selectedId);
      setInfo(res);
    } finally {
      setHealing(false);
    }
  };

  const active = info?.config?.status === 'connected';

  if (loading) {
    return (
      <div className="bg-background-100 rounded-xl border border-background-200/70 p-4 flex items-center gap-2 text-foreground-500 text-xs">
        <div className="w-3.5 h-3.5 border-2 border-secondary-500 border-t-transparent rounded-full animate-spin"></div>
        Đang tải cấu hình Telegram...
      </div>
    );
  }

  return (
    <div className="bg-background-100 rounded-xl border border-background-200/70 p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-secondary-500/10 flex items-center justify-center shrink-0">
            <i className="ri-telegram-2-line text-secondary-400"></i>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-heading font-semibold text-foreground-900">Telegram 2 chiều</h3>
              <span className={cn(
                'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium',
                active ? 'bg-emerald-500/10 text-emerald-500' : 'bg-background-200/70 text-foreground-400'
              )}>
                <span className={cn('w-1.5 h-1.5 rounded-full', active ? 'bg-emerald-400 animate-pulse' : 'bg-foreground-300')}></span>
                {active ? 'Đang bật' : 'Đang tắt'}
              </span>
            </div>
            <p className="text-[11px] text-foreground-500 mt-0.5 truncate">
              Gửi đề xuất vào lệnh kèm nút duyệt, nhận phản hồi trực tiếp từ Telegram.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={selectedId ?? ''}
            onChange={(e) => setSelectedId(Number(e.target.value))}
            className="px-3 py-2 bg-background-50 border border-background-300/60 rounded-lg text-foreground-900 text-xs focus:outline-none focus:border-secondary-500/50 cursor-pointer"
          >
            {accounts.length === 0 && <option value="">Chưa có tài khoản</option>}
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.name} · {a.chat_id}</option>
            ))}
          </select>

          {!active ? (
            <button
              type="button"
              onClick={handleActivate}
              disabled={busy || !selectedId}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-secondary-500 hover:bg-secondary-600 text-background-50 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-play-circle-line"></i>}
              Bật Telegram
            </button>
          ) : (
            <button
              type="button"
              onClick={handleDeactivate}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-background-200/70 hover:bg-background-300/60 text-foreground-600 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-stop-circle-line"></i>}
              Ngắt Telegram
            </button>
          )}
        </div>
      </div>

      {active && info?.info?.url && (
        <div className="mt-3 pt-3 border-t border-background-200/70 flex items-center gap-4 flex-wrap text-[10px] text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-link"></i> Webhook đã đăng ký
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-time-line"></i> Pending: {info.info.pending_update_count ?? 0}
          </span>
          {info.info.last_error_message && (
            <span className="inline-flex items-center gap-1 text-amber-500">
              <i className="ri-error-warning-line"></i> {info.info.last_error_message}
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <i className="ri-shield-check-line"></i> Tự động đăng ký lại khi lỗi
          </span>
          <button
            type="button"
            onClick={handleForceHeal}
            disabled={healing || !selectedId}
            title="Kiểm tra webhook ngay và tự đăng ký lại nếu phát hiện lỗi"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-secondary-500/40 text-secondary-500 hover:bg-secondary-500/10 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {healing ? (
              <i className="ri-loader-4-line animate-spin"></i>
            ) : (
              <i className="ri-stethoscope-line"></i>
            )}
            {healing ? 'Đang kiểm tra...' : 'Kiểm tra & sửa webhook ngay'}
          </button>
          {health && !health.healthy && (
            <span className="inline-flex items-center gap-1 text-amber-500">
              <i className="ri-refresh-line"></i>
              {health.healed ? 'Vừa tự phục hồi webhook' : (health.reasons[0] || 'Đang tự phục hồi webhook...')}
            </span>
          )}
        </div>
      )}

      {msg && (
        <div className={cn(
          'mt-3 rounded-lg px-3 py-2 text-[11px] flex items-start gap-2',
          msg.type === 'ok' ? 'bg-primary-500/10 border border-primary-500/20 text-primary-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'
        )}>
          <i className={cn('text-sm shrink-0 mt-px', msg.type === 'ok' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line')}></i>
          <span className="break-words">{msg.text}</span>
        </div>
      )}
    </div>
  );
}